import { Chip } from "@heroui/react";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { APPROVED, COMPLETED, IN_TRANSIT, STATUS } from "src/constants/statusConstants";
import { UAEStatesMap } from "src/data/States";

export interface IOrderCardProps {
  order: any;
  activeOrder?: any;
  onClick?: (e: any) => void;
}

export function OrderCard({
  order,
  activeOrder,
  onClick = () => { },
}: IOrderCardProps) {
  const { t } = useTranslation();

  const orderNumbers =
    order.orderNumber &&
      order.orderNumber !== "N/A" &&
      !/^0+$/.test(order.orderNumber)
      ? order.orderNumber.split(",")
      : [`${t("order_details.data_not_provided")}`];

  const shippingCompanyName = order.shippingCompany?.name
    ? order.shippingCompany.name.length > 15
      ? `${order.shippingCompany.name.substring(0, 15)}...`
      : order.shippingCompany.name
    : "N/A";

  return (
    <div className={`max-w-[400px] bg-white/[0.04] text-white rounded-lg mb-4 hover:shadow-xl cursor-pointer ${activeOrder === order._id ? "border border-white/[0.15]" : ""
      }`} onClick={onClick}>
      <div className="grid grid-cols-6 gap-3 p-4 pb-2">
        <div>
          <img src="assets/icons/orders/order_logo.svg" alt="logo" />
        </div>
        <div className="col-span-3">
          <p className="text-xs text-secondary">
            {t("orders_page.order_number")}
          </p>
          <p className="text-base font-bold">
            {orderNumbers.map((number: string, index: number) => (
              <React.Fragment key={index}>
                <span
                  className={`${number === `${t("order_details.data_not_provided")}`
                      ? "text-danger text-sm font-normal"
                      : "text-base font-bold"
                    }`}
                >
                  {number}
                </span>
                {index < orderNumbers.length - 1 && (
                  <span className="text-base">,</span>
                )}
                <br />
              </React.Fragment>
            ))}
          </p>
        </div>
        <div className="col-span-2 text-right">
          {order.status && (
            <Chip variant="faded" size="md" className={`py-1 px-2 w-fit ml-auto rounded-full font-semibold text-white ${order.status === COMPLETED ||
                order.status === "complete" ||
                order.status === "reached" ||
                order.status === APPROVED
                ? "bg-success"
                : order.status === IN_TRANSIT
                  ? "bg-warning"
                  : order.status === "Not started"
                    ? "bg-danger"
                    : "bg-gray-100"
              }`}>{STATUS[order.status ? order.status : ""]?.name}</Chip>
          )}
        </div>
      </div>
      <div className="grid grid-cols-6 gap-3 pl-4 pr-4 pb-2">
        <div></div>
        <div className="col-span-4">
          <p className="text-xs text-secondary">
            {t("order_details.truck_number")}
          </p>
          <p className="text-base font-bold">
            {(order.vehicle?.plate?.emirates
              ? `${UAEStatesMap[order.vehicle.plate.emirates]?.code || ""}-`
              : "") +
              (order.vehicle?.plateNumber?.replace("private-", "") || "N/A")}
          </p>
        </div>
      </div>
      <hr className="opacity-10" />
      <div className="grid items-center grid-cols-5 p-4">
        <div className="grid col-span-4 grid-rows-2 gap-6">
          <div className="grid grid-cols-12 gap-3">
            <div className="relative col-span-2">
              <img
                src="/assets/icons/start_icon.svg"
                alt="start"
                className="mx-auto"
              />
              <img
                src="/assets/icons/line-dotted.svg"
                alt="start"
                className="z-10 mx-auto line-dotted-orders"
              />
            </div>
            <div className="col-span-10">
              <p className="text-xs text-secondary">
                {order?.status === "in_transit" || order?.status === "completed"
                  ? t("orders_page.starting_time")
                  : t("orders_page.created_at")}
              </p>
              <p className="h-4 text-sm">
                {(() => {
                  const dateToDisplay = order?.startedAt || order?.createdAt;
                  return dateToDisplay
                    ? moment(dateToDisplay).format("Do MMM YYYY, h:mm a")
                    : "N/A";
                })()}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-2">
              <img
                src="/assets/icons/end_icon.svg"
                alt="start"
                className="mx-auto"
              />
            </div>
            <div className="col-span-10">
              <p className="text-xs text-secondary">
                {t("orders_page.expected_at")}
              </p>
              <p className="h-4 text-sm">
                {order?.status === "completed" && order?.completedAt
                  ? moment(order.completedAt).format("Do MMM YYYY, h:mm a")
                  : order?.expectedAt
                    ? moment(order.expectedAt).format("Do MMM YYYY, h:mm a")
                    : "N/A"}
              </p>
            </div>
          </div>
        </div>
      </div>
      <hr className="opacity-10" />
      <div className="grid grid-cols-6 gap-3 p-4">
        <div className="bg-black-200/[0.7] rounded-lg max-h-11">
          <p className="pt-1 text-3xl font-bold text-center place-self-center">
            {"S"}
          </p>
        </div>
        <div className="col-span-4">
          <p className="text-sm text-secondary">
            {t("orders_page.shipping_company")}
          </p>
          <p
            className="text-sm font-bold"
            data-tooltip-id="tooltip"
            data-tooltip-content={order.shippingCompany?.name}
          >
            {shippingCompanyName}
          </p>
        </div>
      </div>
    </div>
  );
}
